import {
  CurrenciesListListData,
  currenciesListListInfiniteOptions,
  Dropdown,
  invoicesCreateMutation,
  invoicesListQueryKey,
  PaginatedDropdownList,
  ProductsListListData,
  productsListListInfiniteOptions,
  ReturnShortContactReadable
} from '@crm/api'
import { AppLink } from '@crm/components'
import { InfiniteSelect } from '@crm/components'
import {
  AmountField,
  ContactsInvoiceForm
} from '@crm/features/billing/components'
import { useStyles } from '@crm/features/billing/styles'
import { initialEmailHtml } from '@crm/features/billing/utils'
import { useAppNavigate } from '@crm/hooks/useAppNavigate'
import { useDefaultCurrency } from '@crm/hooks/useDefaultCurrency'
import { getRoute } from '@crm/utils'
import { TipTapEditor } from '@shared/components/TipTapEditor'
import { useInvalidatePredicateQueries, useSessionContext } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation } from '@tanstack/react-query'
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Flex,
  Form,
  Input,
  notification,
  Row,
  Tooltip,
  Typography
} from 'antd'
import { useEffect, useState } from 'react'

const { Title } = Typography

type Props = {
  formName: string
}

const defaultFormValues = { valid_till: 4, remind_periodicity: 3 }

export const InvoiceForm = ({ formName }: Props) => {
  const { invalidateQueries } = useInvalidatePredicateQueries()
  const navigate = useAppNavigate()
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { styles } = useStyles()
  const { defaultCurrency } = useDefaultCurrency()

  const { user } = useSessionContext()
  const [contractorId, setContractorId] = useState<number>()

  const title = Form.useWatch('title', form)
  const amounts = Form.useWatch('amounts', form)
  const currency = Form.useWatch('currency', form) || defaultCurrency
  const validTill = Form.useWatch('valid_till', form)
  const invoiceNumber = Form.useWatch('invoice_number', form)

  const { mutate: createMutation } = useMutation({
    ...invoicesCreateMutation(),
    onError: (error) => showApiErrors(error, form),
    onSuccess: () => {
      invalidateQueries([invoicesListQueryKey()])
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_save')
      })
      navigate(getRoute('BillingInvoices'))
    }
  })

  useEffect(() => {
    form.setFieldsValue({
      ...defaultFormValues,
      currency: defaultCurrency
    })
  }, [defaultCurrency])

  useEffect(() => {
    const emailMessage = initialEmailHtml(
      {
        title,
        valid_till: validTill,
        invoice_number: invoiceNumber
      },
      amounts,
      currency,
      user
    )

    const currentEmailMessage = form.getFieldValue('email_message')
    if (currentEmailMessage !== emailMessage) {
      form.setFieldValue('email_message', emailMessage)
    }
  }, [form, title, amounts, currency, validTill, invoiceNumber])

  const onSubmit = async () => {
    const values = await form.validateFields()

    createMutation({
      body: {
        ...values,
        contacts: values?.contacts?.map(
          (contact: ReturnShortContactReadable) => contact
        )
      }
    })
  }

  const onValuesChange = () => {
    const values = form.getFieldsValue()

    if (values?.lead) {
      setContractorId(values?.lead)

      if (values?.lead === undefined) {
        setContractorId(undefined)
        form.setFieldValue('contacts', [])
        form.setFieldValue('additional_emails', [])
      }
    }
  }

  return (
    <Card className={styles.card}>
      <Form
        form={form}
        name={formName}
        onFinish={onSubmit}
        onValuesChange={onValuesChange}
        layout="vertical"
      >
        <Title level={5}>{t('billing.product_details')}</Title>

        <Row justify="space-between" gutter={12}>
          <Col span={12}>
            <Form.Item
              label={t('billing.product_name')}
              name="product"
              required
              rules={[
                {
                  required: true,
                  message: t('billing.product_name_required')
                }
              ]}
            >
              <InfiniteSelect<
                ProductsListListData,
                PaginatedDropdownList,
                Dropdown
              >
                popupMatchSelectWidth
                placeholder={t('billing.select')}
                getQueryFunction={productsListListInfiniteOptions}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('billing.title')}
              name="title"
              required
              rules={[
                {
                  required: true,
                  message: t('billing.title_required')
                }
              ]}
            >
              <Input type="text" placeholder={t('billing.title')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label={t('billing.currency')}
          name="currency"
          required
          rules={[
            {
              required: true,
              message: t('billing.currency_required')
            }
          ]}
        >
          <InfiniteSelect<
            CurrenciesListListData,
            PaginatedDropdownList,
            Dropdown
          >
            popupMatchSelectWidth
            placeholder={t('billing.currency')}
            getQueryFunction={currenciesListListInfiniteOptions}
          />
        </Form.Item>

        <AmountField setFieldValue={form.setFieldValue} />

        <Form.Item
          label={t('billing.invoice')}
          name="invoice_number"
          required
          rules={[
            {
              required: true,
              message: t('billing.invoice_required')
            }
          ]}
        >
          <Input placeholder={t('billing.invoice')} />
        </Form.Item>

        <Divider />

        <Row>
          <Col span={24}>
            <ContactsInvoiceForm
              form={form}
              contractorId={contractorId}
              setContractorId={setContractorId}
            />
          </Col>
        </Row>

        <Divider />

        <Title level={5}>{t('billing.deadline')}</Title>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item label={t('billing.valid_for')} name="valid_till">
              <Input type="number" placeholder={t('billing.valid_for')} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item
              label={t('billing.remind_in')}
              name="remind_periodicity"
              required
              rules={[
                {
                  required: true,
                  message: t('billing.remind_periodicity_required')
                }
              ]}
            >
              <Input
                type="number"
                placeholder={t('billing.remind_in')}
                suffix={<Tooltip title={t('billing.days')}></Tooltip>}
              />
            </Form.Item>
          </Col>
        </Row>

        <Divider />

        <Title level={5}>{t('billing.email_message')}</Title>

        <Form.Item name="email_message">
          <TipTapEditor />
        </Form.Item>

        <Form.Item
          label={t('billing.send_email')}
          name="send_email"
          valuePropName="checked"
        >
          <Checkbox />
        </Form.Item>

        <Flex gap={10} justify="end">
          <AppLink to={getRoute('BillingInvoices')}>
            <Button>{t('buttons.cancel')}</Button>
          </AppLink>

          <Button type="primary" htmlType="submit" form={formName}>
            {t('buttons.save')}
          </Button>
        </Flex>
      </Form>
    </Card>
  )
}
